import { useLocale as useLocaleR42 } from '@/lib/locale';
/**
 * ContactCoachDialog — Phase 73j (June 2026)
 *
 * Replaces the bare `mailto:` / `tel:` anchor on coach landing pages.
 * Wingman regression report flagged that on mobile devices without a
 * configured email app, the `mailto:` button silently does nothing —
 * parents tap it, see no response, and either retry or bounce.
 *
 * The dialog surfaces THREE always-working paths to reach the coach:
 *   1. **Phone (tel: link)** — opens dialer on mobile. Hidden if the
 *      coach hasn't set a phone number.
 *   2. **Email (mailto: link + Copy button)** — the mailto: still works
 *      for users with a configured email client, but the Copy-to-clipboard
 *      button is ALWAYS reliable (Safari, in-app browsers, kiosks…).
 *      Subject + body are pre-templated so the coach receives an
 *      identifiable "interested in lessons" message.
 *   3. **SMS (sms: link)** — bonus channel; opens native messaging
 *      app with a pre-filled inquiry. Hidden if no phone set.
 *
 * Naming: we use named `ContactCoachDialog` AND a default export so
 * the host page can import either way. Dialog is fully keyboard-
 * navigable via the underlying shadcn Dialog primitive.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Phone, Mail, MessageSquare, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactCoachDialog({ open, onOpenChange, coach, primary = '#00d4ff' }) {
    const { t: t42 } = useLocaleR42();
    const [copied, setCopied] = useState(false);
    if (!coach) return null;

    // Strip non-numeric chars for the tel:/sms: hrefs (iOS Safari is strict).
    const phoneClean = (coach.contact_phone || coach.phone || '').replace(/[^\d+]/g, '');
    const phoneDisplay = coach.contact_phone || coach.phone || '';
    const email = coach.contact_email || '';
    const coachName = coach.first_name || coach.display_name || 'your coach';

    const inquirySubject = encodeURIComponent(`Swim lesson inquiry`);
    const inquiryBody = encodeURIComponent(
        `Hi ${coachName},\n\nI saw your Splash Pad page and wanted to ask about swim lessons. Can we talk about availability and pricing?\n\nThanks!`,
    );

    const mailtoUrl = email ? `mailto:${email}?subject=${inquirySubject}&body=${inquiryBody}` : null;
    const smsBody = encodeURIComponent(`Hi ${coachName}, I saw your Splash Pad page — interested in swim lessons. When can we chat?`);
    const smsUrl = phoneClean ? `sms:${phoneClean}?&body=${smsBody}` : null;
    const telUrl = phoneClean ? `tel:${phoneClean}` : null;

    const copyEmail = async () => {
        if (!email) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(email);
            } else {
                const ta = document.createElement('textarea');
                ta.value = email;
                ta.style.position = 'fixed'; ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success(t42("Email copied to clipboard"));
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(t42("Could not copy — select the email text manually."));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="flex max-h-[90vh] max-h-[90dvh] w-[95vw] max-w-md flex-col gap-0 p-0 sm:w-full"
                data-testid="contact-coach-dialog"
            >
                <DialogHeader className="shrink-0 border-b border-[#0a1628]/5 px-5 py-4 sm:px-6">
                    <DialogTitle>{t42("Get in touch with")} {coachName}</DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm"> {t42("Pick the channel that works for you.")} {coachName} {t42("usually responds within a day.")} </DialogDescription>
                </DialogHeader>

                <div className="flex-1 space-y-2 overflow-y-auto px-5 py-4 sm:px-6">
                    {telUrl && (
                        <Button
                            asChild
                            variant="outline"
                            className="h-auto w-full justify-start gap-3 rounded-xl border-[#0a1628]/10 p-3 text-left hover:bg-[#00d4ff]/5"
                            data-testid="contact-coach-call"
                        >
                            <a href={telUrl}>
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: `${primary}25` }}>
                                    <Phone className="h-4 w-4" style={{ color: primary }} />
                                </span>
                                <span className="flex-1">
                                    <span className="block text-sm font-semibold text-[#0a1628]">{t42("Call")}</span>
                                    <span className="block text-xs text-[#1a3a5c]/70">{phoneDisplay}</span>
                                </span>
                                <ExternalLink className="h-3.5 w-3.5 text-[#1a3a5c]/40" />
                            </a>
                        </Button>
                    )}

                    {smsUrl && (
                        <Button
                            asChild
                            variant="outline"
                            className="h-auto w-full justify-start gap-3 rounded-xl border-[#0a1628]/10 p-3 text-left hover:bg-[#00d4ff]/5"
                            data-testid="contact-coach-text"
                        >
                            <a href={smsUrl}>
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: `${primary}25` }}>
                                    <MessageSquare className="h-4 w-4" style={{ color: primary }} />
                                </span>
                                <span className="flex-1">
                                    <span className="block text-sm font-semibold text-[#0a1628]">{t42("Text")}</span>
                                    <span className="block text-xs text-[#1a3a5c]/70">{t42("Opens your messaging app")}</span>
                                </span>
                                <ExternalLink className="h-3.5 w-3.5 text-[#1a3a5c]/40" />
                            </a>
                        </Button>
                    )}

                    {email && (
                        <div className="rounded-xl border border-[#0a1628]/10 p-3" data-testid="contact-coach-email-block">
                            <div className="flex items-center gap-3">
                                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={{ background: `${primary}25` }}>
                                    <Mail className="h-4 w-4" style={{ color: primary }} />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-[#0a1628]">{t42("Email")}</div>
                                    <div className="truncate text-xs text-[#1a3a5c]/70">{email}</div>
                                </div>
                            </div>
                            <div className="mt-2.5 grid grid-cols-2 gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={copyEmail}
                                    className="h-9 rounded-lg"
                                    data-testid="contact-coach-copy-email"
                                >
                                    {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                                    {copied ? t42("Copied") : t42("Copy")}
                                </Button>
                                <Button
                                    asChild
                                    size="sm"
                                    className="sd-focus h-9 rounded-lg text-[#0a1628]"
                                    style={{ background: primary, fontWeight: 600 }}
                                    data-testid="contact-coach-open-email"
                                >
                                    <a href={mailtoUrl}> {t42("Open email app")} </a>
                                </Button>
                            </div>
                            <div className="mt-1.5 text-[10px] text-[#1a3a5c]/60"> {t42("\"Open email app\" only works if you have one configured. Otherwise, tap")} <strong>{t42("Copy")}</strong> {t42("and paste it into your inbox.")} </div>
                        </div>
                    )}

                    {!telUrl && !email && (
                        <div className="rounded-xl border border-[#ff6b35]/30 bg-[#ff6b35]/5 p-3 text-xs text-[#ff6b35]"> {t42("This coach hasn't published a way to reach them yet. Tap")} <strong>{t42("Apply Now")}</strong> {t42("on the main page to start the application — they'll be in touch.")} </div>
                    )}
                </div>

                <DialogFooter className="shrink-0 border-t border-[#0a1628]/5 px-5 py-3 sm:px-6">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="contact-coach-close">{t42("Close")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
